import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Input,
  Paper,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Alert
} from '@mui/material';
import { MdCloudUpload, MdCheckCircle } from 'react-icons/md';
import MapPicker from '../../components/MapPicker';
import { indianStates } from '../../Utils/states';

const SellerRegisterForm = ({
  sellerForm,
  handleFormChange,
  handleFileUpload,
  errors = {},
  setErrors = () => { },
  handleRegisterSeller,
  handleSaveDraft,
  isSubmitting
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedDocType, setSelectedDocType] = useState('');

  const steps = ['Business Information', 'Contact Persons', 'Documents', 'Bank Details', 'Store Info'];

  const docTypes = [
    { id: 'addressProof', label: 'Address Proof (Utility Bill/Rent Agreement)' },
    { id: 'gstProof', label: 'GST Registration Certificate' },
    { id: 'businessPanProof', label: 'Business PAN Card' },
    { id: 'shopActLicense', label: 'Shop & Establishment License' },
    { id: 'msmeUdyamCertificate', label: 'MSME / Udyam Certificate' },
    { id: 'cancelledCheque', label: 'Cancelled Cheque' },
    { id: 'eSignature', label: 'Electronic Signature / Stamp' },
  ];

  // Validate individual field
  const validateField = (name, value) => {
    let error = '';

    switch (name) {
      case 'name':
        if (!value.trim()) error = 'Name is required';
        break;
      case 'email':
        if (!value.trim()) error = 'Email is required';
        else if (!/^\S+@\S+\.\S+$/.test(value)) error = 'Invalid email format';
        break;
      case 'password':
        if (!value) error = 'Password is required';
        else if (value.length < 6) error = 'Password must be at least 6 characters';
        break;
      case 'mobile':
        if (!value.trim()) error = 'Mobile number is required';
        else if (!/^\d{10}$/.test(value.replace(/\D/g, ''))) error = 'Enter valid 10-digit mobile number';
        break;
      case 'business_name':
        if (!value.trim()) error = 'Business name is required';
        break;
      case 'businessAddress':
        if (!value.trim()) error = 'Business address is required';
        break;
      case 'businessCity':
        if (!value.trim()) error = 'City is required';
        break;
      case 'businessState':
        if (!value.trim()) error = 'State is required';
        break;
      case 'businessPincode':
        if (!value.trim()) error = 'Pincode is required';
        else if (!/^\d{6}$/.test(value)) error = 'Pincode must be 6 digits';
        break;
      case 'gstNumber':
        if (value.trim() && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(value))
          error = 'Invalid GST format';
        break;
      case 'businessPAN':
        if (value.trim() && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value)) error = 'Invalid PAN format';
        break;
      case 'bankIfsc':
        if (value.trim() && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value)) error = 'Invalid IFSC format';
        break;
      case 'contactPerson2Email':
        if (value.trim() && !/^\S+@\S+\.\S+$/.test(value)) error = 'Invalid email format';
        break;
      case 'contactPerson2Mobile':
        if (value.trim() && !/^\d{10}$/.test(value.replace(/\D/g, ''))) error = 'Enter valid 10-digit mobile number';
        break;
      case 'contactPerson2Password':
        if (sellerForm.contactPerson2Name && !value) error = 'Password is required for access';
        else if (value && value.length < 6) error = 'Password must be at least 6 characters';
        break;
      default:
        break;
    }

    return error;
  };

  const handleInputChange = (e) => {
    let { name, value } = e.target;
    if (name === 'gstNumber' || name === 'businessPAN' || name === 'bankIfsc') {
      value = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      e.target.value = value;
    }
    handleFormChange(e);
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files[0];

    if (!file) {
      setErrors(prev => ({ ...prev, [name]: 'File is required' }));
      handleFileUpload(e);
      return;
    }

    if (!file.type.startsWith('image/') && !file.type.includes('pdf')) {
      setErrors(prev => ({ ...prev, [name]: 'Only images & PDF allowed' }));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, [name]: 'File size must be under 5MB' }));
      return;
    }

    setErrors(prev => ({ ...prev, [name]: undefined }));
    handleFileUpload(e);
  };

  const fetchAddressFromLatLng = async (lat, lng) => {
    try {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY; // Using the key from environment variables
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`
      );
      const data = await response.json();
      
      if (data.status !== 'OK') {
        console.error('Google Geocoding error:', data.status);
        return null;
      }

      const result = data.results[0];
      const addressComponents = result.address_components;
      
      const getComponent = (type) => {
        const comp = addressComponents.find(c => c.types.includes(type));
        return comp ? comp.long_name : '';
      };

      return {
        storeAddress: result.formatted_address || '',
        storeArea: getComponent('sublocality_level_1') || getComponent('locality') || '',
        storeNearbyLandmark: getComponent('neighborhood') || '',
        storeCity: getComponent('locality') || getComponent('administrative_area_level_2') || '',
        storeState: getComponent('administrative_area_level_1') || '',
        storePincode: getComponent('postal_code') || '',
        storeLatitude: lat,
        storeLongitude: lng
      };
    } catch (error) {
      console.error('Error fetching address:', error);
      return null;
    }
  };

  const renderViewField = (label, value) => (
    <Box sx={{ p: 1 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography>{value || '-'}</Typography>
    </Box>
  );

  const handleNext = () => {
    // Basic validation before next
    if (activeStep === 0) {
      const req = ['business_name', 'businessAddress', 'businessCity', 'businessState', 'businessPincode', 'sellerLicenseNumber'];
      const firstError = req.find(f => !sellerForm[f]);
      if (firstError) {
        setErrors(prev => ({ ...prev, [firstError]: 'Required' }));
        return;
      }
    }
    if (activeStep === 2) {
      const isAnyFileUploaded = docTypes.some(doc => !!sellerForm[doc.id]);
      if (!isAnyFileUploaded) {
        setErrors(prev => ({ ...prev, documents: 'At least one document is required' }));
        return;
      } else {
        setErrors(prev => ({ ...prev, documents: null }));
      }
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: 'rgba(255, 255, 255, 0.95)', borderRadius: '24px', boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 6 }}>
        {steps.map((label) => (
          <Step key={label} sx={{
            '& .MuiStepLabel-label': { fontFamily: 'Mulish, sans-serif', fontWeight: 600 },
            '& .MuiStepIcon-root.Mui-active': { color: 'rgb(255, 209, 2)' },
            '& .MuiStepIcon-root.Mui-completed': { color: '#2e7d32' }
          }}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ minHeight: '400px' }}>
        {activeStep === 0 && (
          <>
            <Box sx={{ borderLeft: '4px solid rgb(255, 209, 2)', pl: 2, mb: 4 }}>
              <Typography variant="h5" fontWeight={800} sx={{ fontFamily: 'Mulish, sans-serif' }}>
                Business Information
              </Typography>
              <Typography variant="body2" color="text.secondary">Provide your official business registration details</Typography>
            </Box>
            <Grid container spacing={3} mb={3}>
              {['business_name', 'sellerLicenseNumber', 'gstNumber', 'businessPAN', 'businessAddress', 'businessCity', 'businessState', 'businessPincode'].map(field => (
                <Grid item xs={12} sm={6} md={field === 'businessAddress' ? 8 : 4} key={field}>
                  <TextField
                    fullWidth
                    select={field === 'businessState'}
                    label={(() => {
                      switch (field) {
                        case 'businessAddress':
                        case 'sellerLicenseNumber':
                          return field.replace(/([A-Z])/g, ' $1').trim().replace(/^\w/, c => c.toUpperCase());
                        default:
                          return field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                      }
                    })()}
                    name={field}
                    value={sellerForm[field] || ''}
                    onChange={handleInputChange}
                    required={!['gstNumber', 'businessPAN'].includes(field)}
                    error={!!errors[field]}
                    helperText={errors[field]}
                    InputProps={{
                      sx: { borderRadius: '12px' }
                    }}
                  >
                    {field === 'businessState' && indianStates.map((state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {activeStep === 1 && (
          <>
            <Box sx={{ borderLeft: '4px solid rgb(255, 209, 2)', pl: 2, mb: 4 }}>
              <Typography variant="h5" fontWeight={800} sx={{ fontFamily: 'Mulish, sans-serif' }}>
                Contact Persons
              </Typography>
              <Typography variant="body2" color="text.secondary">Add people who will manage the seller account</Typography>
            </Box>
            
            <Box sx={{ p: 3, border: '1px solid #eee', borderRadius: '16px', mb: 3, bgcolor: '#fafafa' }}>
              <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 800, color: '#0d47a1', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#0d47a1' }} />
                Primary Contact (Admin)
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField fullWidth label="Full Name" name="name" value={sellerForm.name || ''}
                    onChange={handleInputChange} required
                    error={!!errors.name} helperText={errors.name} InputProps={{ sx: { borderRadius: '12px' } }} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField fullWidth label="Email Address" name="email" type="email" value={sellerForm.email || ''}
                    onChange={handleInputChange} required
                    error={!!errors.email} helperText={errors.email} InputProps={{ sx: { borderRadius: '12px' } }} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField fullWidth label="Mobile Number" name="mobile" value={sellerForm.mobile || ''}
                    onChange={handleInputChange} required
                    error={!!errors.mobile} helperText={errors.mobile} InputProps={{ sx: { borderRadius: '12px' } }} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField fullWidth label="Account Password" name="password" type="password" value={sellerForm.password || ''}
                    onChange={handleInputChange} required
                    error={!!errors.password} helperText={errors.password} InputProps={{ sx: { borderRadius: '12px' } }} />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ p: 3, border: '1px solid #eee', borderRadius: '16px', mb: 3, bgcolor: '#fafafa' }}>
              <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 800, color: '#f57c00', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#f57c00' }} />
                Secondary Contact (Optional Access)
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField fullWidth label="Full Name" name="contactPerson2Name" value={sellerForm.contactPerson2Name || ''}
                    onChange={handleInputChange}
                    error={!!errors.contactPerson2Name} helperText={errors.contactPerson2Name} InputProps={{ sx: { borderRadius: '12px' } }} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField fullWidth label="Email Address" name="contactPerson2Email" type="email" value={sellerForm.contactPerson2Email || ''}
                    onChange={handleInputChange}
                    error={!!errors.contactPerson2Email} helperText={errors.contactPerson2Email} InputProps={{ sx: { borderRadius: '12px' } }} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField fullWidth label="Mobile Number" name="contactPerson2Mobile" value={sellerForm.contactPerson2Mobile || ''}
                    onChange={handleInputChange}
                    error={!!errors.contactPerson2Mobile} helperText={errors.contactPerson2Mobile} InputProps={{ sx: { borderRadius: '12px' } }} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField fullWidth label="Login Password" name="contactPerson2Password" type="password" value={sellerForm.contactPerson2Password || ''}
                    onChange={handleInputChange}
                    error={!!errors.contactPerson2Password} helperText={errors.contactPerson2Password} InputProps={{ sx: { borderRadius: '12px' } }} />
                </Grid>
              </Grid>
            </Box>
          </>
        )}

        {activeStep === 2 && (
          <>
            <Box sx={{ borderLeft: '4px solid rgb(255, 209, 2)', pl: 2, mb: 6 }}>
              <Typography variant="h5" fontWeight={800} sx={{ fontFamily: 'Mulish, sans-serif' }}>
                Documents Vault
              </Typography>
              <Typography variant="body2" color="text.secondary">Upload required verification documents</Typography>
            </Box>
            
            {errors.documents && (
              <Alert severity="error" sx={{ mb: 4, borderRadius: '12px' }}>{errors.documents}</Alert>
            )}

            <Grid container spacing={4} sx={{ px: { xs: 0, md: 2 } }} mb={6}>
              {docTypes.map(doc => (
                <Grid item xs={12} sm={6} md={4} key={doc.id} sx={{ display: 'flex' }}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 4, 
                      width: '100%',
                      border: '1px solid', 
                      borderColor: sellerForm[doc.id] ? '#4caf50' : '#e0e0e0',
                      borderRadius: '20px', 
                      display: 'flex', 
                      flexDirection: 'column',
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      textAlign: 'center',
                      bgcolor: sellerForm[doc.id] ? '#f1f8e9' : '#fff',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.04)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      minHeight: '280px',
                      '&:hover': {
                        borderColor: 'rgb(255, 209, 2)',
                        transform: 'translateY(-5px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
                        bgcolor: '#fff'
                      }
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography 
                        variant="subtitle1" 
                        fontWeight={800} 
                        sx={{ 
                          mb: 2, 
                          fontFamily: 'Mulish, sans-serif',
                          lineHeight: 1.3,
                          height: '48px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {doc.label}
                      </Typography>
                      
                      {sellerForm[doc.id] ? (
                        <Box sx={{ mb: 3, py: 1, bgcolor: 'rgba(76, 175, 80, 0.1)', borderRadius: '12px' }}>
                          <Typography variant="body2" color="success.main" fontWeight={900} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <MdCheckCircle size={20} /> READY
                          </Typography>
                          <Typography variant="caption" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', maxWidth: '100%', px: 1, mt: 0.5 }}>
                            {sellerForm[doc.id].name}
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ mb: 3, opacity: 0.2 }}>
                          <MdCloudUpload size={64} />
                        </Box>
                      )}
                    </Box>

                    <Button
                      component="label"
                      variant={sellerForm[doc.id] ? "outlined" : "contained"}
                      color={sellerForm[doc.id] ? "success" : "primary"}
                      fullWidth
                      sx={{ 
                        borderRadius: '14px',
                        py: 1.5,
                        textTransform: 'none',
                        fontWeight: 800,
                        fontSize: '0.9rem',
                        ...( !sellerForm[doc.id] && {
                          bgcolor: 'rgb(255, 209, 2)',
                          color: '#000',
                          boxShadow: '0 4px 12px rgba(255, 209, 2, 0.2)',
                          '&:hover': { bgcolor: 'rgb(240, 190, 0)', boxShadow: '0 6px 20px rgba(255, 209, 2, 0.3)' }
                        })
                      }}
                    >
                      {sellerForm[doc.id] ? "Update File" : "Choose File"}
                      <Input type="file" name={doc.id} onChange={handleFileChange} style={{ display: 'none' }} />
                    </Button>
                    {errors[doc.id] && <Typography color="error" variant="caption" sx={{ mt: 1, fontWeight: 600 }}>{errors[doc.id]}</Typography>}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {activeStep === 3 && (
          <>
            <Box sx={{ borderLeft: '4px solid rgb(255, 209, 2)', pl: 2, mb: 4 }}>
              <Typography variant="h5" fontWeight={800} sx={{ fontFamily: 'Mulish, sans-serif' }}>
                Bank Details
              </Typography>
              <Typography variant="body2" color="text.secondary">Enter bank account details for payouts</Typography>
            </Box>
            <Grid container spacing={3} mb={3}>
              {[
                { name: 'bankAccountHolder', label: 'Account Holder Name' },
                { name: 'bankAccountNumber', label: 'Account Number' },
                { name: 'bankIfsc', label: 'IFSC Code' },
                { name: 'bankName', label: 'Bank Name' },
                { name: 'bankUpiId', label: 'UPI ID (Optional)' },
              ].map(field => (
                <Grid item xs={12} sm={6} md={field.name === 'bankUpiId' ? 12 : 6} key={field.name}>
                  <TextField
                    fullWidth
                    label={field.label}
                    name={field.name}
                    value={sellerForm[field.name] || ''}
                    onChange={handleInputChange}
                    required={field.name !== 'bankUpiId'}
                    error={!!errors[field.name]}
                    helperText={errors[field.name]}
                    InputProps={{ sx: { borderRadius: '12px' } }}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {activeStep === 4 && (
          <>
            <Box sx={{ borderLeft: '4px solid rgb(255, 209, 2)', pl: 2, mb: 4 }}>
              <Typography variant="h5" fontWeight={800} sx={{ fontFamily: 'Mulish, sans-serif' }}>
                Store Presence
              </Typography>
              <Typography variant="body2" color="text.secondary">Set your store location and operational details</Typography>
            </Box>
            <Grid container spacing={3} mb={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Store Description"
                  name="storeDescription"
                  value={sellerForm.storeDescription || ''}
                  onChange={handleFormChange}
                  sx={{ mb: 3 }}
                  InputProps={{ sx: { borderRadius: '12px' } }}
                  placeholder="Tell us about your store, products and experience..."
                />

                <Paper variant="outlined" sx={{ p: 2, borderRadius: '16px', borderStyle: 'dashed', bgcolor: '#fdfdfd' }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'red' }} />
                    Pin exact store location on map
                  </Typography>
                  {errors.storeLatitude && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{errors.storeLatitude}</Alert>}
                  <Box sx={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #ddd' }}>
                    <MapPicker
                      lat={sellerForm.storeLatitude}
                      lng={sellerForm.storeLongitude}
                      onChange={async ({ lat, lng }) => {
                        const addr = await fetchAddressFromLatLng(lat, lng);
                        if (addr) {
                          Object.entries(addr).forEach(([name, value]) => {
                            handleFormChange({ target: { name, value } });
                          });
                          setErrors(prev => ({ ...prev, storeLatitude: undefined }));
                        }
                      }}
                    />
                  </Box>
                </Paper>

                <Grid container spacing={3} mt={2}>
                  {['storeAddress', 'storeArea', 'storeNearbyLandmark', 'storeCity', 'storeState', 'storePincode'].map(field => (
                    <Grid item xs={12} sm={6} md={field === 'storeAddress' ? 12 : 4} key={field}>
                      <TextField
                        fullWidth
                        select={field === 'storeState'}
                        label={field.replace('store', '').replace(/([A-Z])/g, ' $1').trim()}
                        name={field}
                        value={sellerForm[field] || ''}
                        onChange={handleFormChange}
                        multiline={field === 'storeAddress' && field !== 'storeState'}
                        rows={field === 'storeAddress' ? 2 : 1}
                        required={['storeAddress', 'storeCity', 'storeState', 'storePincode'].includes(field)}
                        InputProps={{ sx: { borderRadius: '12px' } }}
                      >
                        {field === 'storeState' && indianStates.map((state) => (
                          <MenuItem key={state} value={state}>
                            {state}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  ))}
                  <Grid item xs={12} sm={6} md={6}>
                    <TextField fullWidth label="Latitude" value={sellerForm.storeLatitude || ''} InputProps={{ readOnly: true, sx: { borderRadius: '12px', bgcolor: '#f5f5f5' } }} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <TextField fullWidth label="Longitude" value={sellerForm.storeLongitude || ''} InputProps={{ readOnly: true, sx: { borderRadius: '12px', bgcolor: '#f5f5f5' } }} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
      </Box>

      <Divider sx={{ my: 4, opacity: 0.5 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
          variant="outlined"
          sx={{ 
            px: 6, 
            py: 1.5, 
            borderRadius: '12px', 
            textTransform: 'none', 
            fontWeight: 800,
            borderColor: '#000',
            color: '#000',
            '&:hover': { borderColor: '#333', bgcolor: 'rgba(0,0,0,0.05)' }
          }}
        >
          Back
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleSaveDraft}
            disabled={isSubmitting}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 700,
              borderColor: '#111827',
              color: '#111827',
              '&:hover': {
                borderColor: '#000',
                bgcolor: 'rgba(17, 24, 39, 0.05)'
              }
            }}
          >
            Save Draft
          </Button>
          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              sx={{ 
                background: 'rgb(255 209 2)', 
                color: 'black', 
                fontWeight: 800, 
                px: 6, 
                py: 1.5, 
                borderRadius: '12px',
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)',
                '&:hover': { 
                  background: 'rgb(240, 195, 0)', 
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(255, 209, 2, 0.4)' 
                } 
              }}
            >
              Continue to {steps[activeStep + 1]}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleRegisterSeller}
              disabled={isSubmitting}
              sx={{ 
                background: 'rgb(255 209 2)', 
                color: 'black', 
                fontWeight: 900, 
                px: 8, 
                py: 2, 
                borderRadius: '12px',
                textTransform: 'none',
                boxShadow: '0 8px 24px rgba(255, 209, 2, 0.4)',
                '&:hover': { 
                  background: 'rgb(240, 195, 0)', 
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 32px rgba(255, 209, 2, 0.5)' 
                },
                "&:disabled": { background: "#666", color: "#999" }
              }}
            >
              {isSubmitting ? "Submitting..." : "Complete Registration"}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SellerRegisterForm;